import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getEsignTemplates, createEsignTemplate, deleteEsignTemplate,
  createEsignRequest, getEsignRequests, downloadEsignRequestPdf, voidEsignRequest,
  bulkSendEsign, getEsignBatches, getEsignBatch, getMyEsignTasks, getEmployees,
} from "../api";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { useAuth } from "../context/AuthContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { EsignFieldEditor, EsignField, EsignRole } from "../components/EsignFieldEditor";
import { useRouteTab } from "../hooks/useRouteTab";

// Exported so App's header can title bare /esign the way this page actually
// renders it: senders get Send for Signature, everyone else their To Sign
// list. One static PAGE_NAMES entry would mislabel one of the two audiences.
export const SENDER_ROLES = ["admin", "admin_assistant", "hr", "recruiter"];

type Tab = "send" | "templates" | "sent" | "tosign";

const card: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20,
};
const label: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 };
const input: React.CSSProperties = {
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
  border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box",
};
const btnPrimary: React.CSSProperties = {
  padding: "9px 16px", borderRadius: 8, border: "none", background: "#1e3a8a", color: "white",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  padding: "9px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "white",
  color: "#1e293b", fontSize: 13, cursor: "pointer",
};

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  draft: { bg: "#f1f5f9", fg: "#475569" },
  sent: { bg: "#eff6ff", fg: "#1d4ed8" },
  in_progress: { bg: "#fefce8", fg: "#854d0e" },
  completed: { bg: "#f0fdf4", fg: "#15803d" },
  declined: { bg: "#fef2f2", fg: "#dc2626" },
  expired: { bg: "#fef2f2", fg: "#b91c1c" },
  voided: { bg: "#f1f5f9", fg: "#64748b" },
  pending: { bg: "#f1f5f9", fg: "#64748b" },
  notified: { bg: "#eff6ff", fg: "#1d4ed8" },
  viewed: { bg: "#fefce8", fg: "#854d0e" },
  signed: { bg: "#f0fdf4", fg: "#15803d" },
};

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || { bg: "#f1f5f9", fg: "#475569" };
  return (
    <span style={{ background: c.bg, color: c.fg, borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 700, textTransform: "capitalize" }}>
      {status.replace("_", " ")}
    </span>
  );
}

function EmptyState({ icon, title, hint, action }: { icon: string; title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8", background: "#f8fafc", borderRadius: 12, border: "1px dashed #e2e8f0" }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#475569" }}>{title}</div>
      {hint && <div style={{ fontSize: 12, marginTop: 4, color: "#94a3b8", maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>{hint}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export default function EsignPage() {
  const { user } = useAuth() as any;
  const isSender = SENDER_ROLES.includes(user?.role);
  // Tab lives in the URL — the module sidebar is the switcher (spec
  // module-subpage-navs). Bare /esign keeps its role-dependent default:
  // senders land on Send, signers on To Sign.
  const [routeTab] = useRouteTab<Tab>(
    "/esign",
    { send: "", templates: "templates", sent: "sent", tosign: "to-sign" },
    isSender ? "send" : "tosign",
  );
  // A signer following a sender-only deep link degrades to their one real
  // surface instead of an empty page.
  const tab: Tab = isSender ? routeTab : "tosign";

  return (
    <div>
      {/* Width and padding come from ShellMain. This is a list page like
          Candidates, Jobs, Offers and Talent Pool — all full width — so a
          cap here left it visibly narrower than its siblings, and its own
          padding stacked on the shell's. */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: "0 0 4px", fontSize: 22, color: "#0f172a" }}>Document Signing</h1>
        <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
          Upload any document, place signature fields, and send it for signature — separate from Offer Letters and HR Letters.
        </p>
      </div>

      {/* Tab bar removed — the module sidebar is the switcher now. */}

      {tab === "send" && isSender && <SendTab />}
      {tab === "templates" && isSender && <TemplatesTab />}
      {tab === "sent" && isSender && <SentTab />}
      {tab === "tosign" && <ToSignTab />}
    </div>
  );
}

// ── Templates tab ────────────────────────────────────────────────────────────

function TemplatesTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { data: templates = [], isLoading } = useQuery({ queryKey: ["esign-templates"], queryFn: getEsignTemplates });
  const [creating, setCreating] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");

  const createMut = useMutation({
    mutationFn: ({ fields, roles }: { fields: EsignField[]; roles: EsignRole[] }) =>
      createEsignTemplate(file!, name, fields, roles, true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["esign-templates"] });
      toast("Template saved", "success");
      setCreating(false); setFile(null); setName("");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save template", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteEsignTemplate(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["esign-templates"] }); toast("Template deleted", "success"); },
    onError: () => toast("Failed to delete template", "error"),
  });

  if (creating && file) {
    return (
      <div style={card}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#0f172a" }}>Place fields on "{name || file.name}"</h3>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>
          Define who needs to sign, then click the document to place their Signature/Date fields.
        </p>
        <EsignFieldEditor
          file={file}
          onCancel={() => { setCreating(false); setFile(null); }}
          onSave={(fields, roles) => createMut.mutate({ fields, roles })}
        />
        <LoadingOverlay active={createMut.isPending} />
      </div>
    );
  }

  return (
    <div>
      <div style={card}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, color: "#0f172a" }}>New Template</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 220px" }}>
            <label style={label}>Template name</label>
            <input style={input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Standard NDA" />
          </div>
          <div style={{ flex: "1 1 220px" }}>
            <label style={label}>Upload PDF</label>
            <input type="file" accept="application/pdf" style={input}
              onChange={e => setFile(e.target.files?.[0] || null)} />
          </div>
          <button style={btnPrimary} disabled={!file || !name.trim()}
            onClick={() => setCreating(true)}>
            Place fields →
          </button>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, color: "#0f172a" }}>Your Templates</h3>
        {isLoading && <p style={{ fontSize: 13, color: "#64748b" }}>Loading…</p>}
        {!isLoading && templates.length === 0 && (
          <EmptyState icon="📄" title="No templates yet" hint="Upload a PDF above and place its signature fields to create your first reusable template." />
        )}
        <div style={{ display: "grid", gap: 10 }}>
          {(templates as any[]).map(t => (
            <div key={t.id} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{t.name}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {t.roles.length} signer role{t.roles.length !== 1 ? "s" : ""} · {t.fields.length} field{t.fields.length !== 1 ? "s" : ""}
                </div>
              </div>
              <AsyncButton
                style={{ ...btnGhost, color: "#dc2626", borderColor: "#fecaca" }}
                onClick={async () => {
                  if (!(await confirm({ title: "Delete template?", message: `"${t.name}" will no longer be available to send. Requests already sent from it are unaffected.`, confirmLabel: "Delete" }))) return;
                  await deleteMut.mutateAsync(t.id);
                }}>
                Delete
              </AsyncButton>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Send tab ──────────────────────────────────────────────────────────────────

function SendTab() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery({ queryKey: ["esign-templates"], queryFn: getEsignTemplates });
  const { data: employees = [] } = useQuery({ queryKey: ["employees-for-esign"], queryFn: getEmployees });
  const [templateId, setTemplateId] = useState<number | "">("");
  const [title, setTitle] = useState("");
  const [reminderDays, setReminderDays] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [bulkMode, setBulkMode] = useState(false);
  const [recipients, setRecipients] = useState<Record<string, { mode: "internal" | "external"; internal_user_id?: number; external_name?: string; external_email?: string }>>({});
  const [bulkRecipients, setBulkRecipients] = useState<{ mode: "internal" | "external"; internal_user_id?: number; external_name?: string; external_email?: string }[]>([
    { mode: "internal" },
  ]);

  const template = (templates as any[]).find(t => t.id === templateId);
  const canBulk = template && template.roles.length === 1;

  const sendMut = useMutation({
    mutationFn: () => createEsignRequest({
      template_id: templateId as number,
      title: title.trim() || template.name,
      recipients: template.roles.map((r: any) => ({
        role: r.role,
        ...(recipients[r.role]?.mode === "external"
          ? { external_name: recipients[r.role].external_name, external_email: recipients[r.role].external_email }
          : { internal_user_id: recipients[r.role]?.internal_user_id }),
      })),
      reminder_cadence_days: reminderDays ? Number(reminderDays) : undefined,
      expires_at: expiresAt || undefined,
    }),
    onSuccess: () => {
      toast("Sent for signature", "success");
      qc.invalidateQueries({ queryKey: ["esign-requests"] });
      setTemplateId(""); setTitle(""); setRecipients({});
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to send", "error"),
  });

  const bulkSendMut = useMutation({
    mutationFn: () => bulkSendEsign(templateId as number, {
      title: title.trim() || template.name,
      recipients: bulkRecipients.map(r => r.mode === "external"
        ? { external_name: r.external_name, external_email: r.external_email }
        : { internal_user_id: r.internal_user_id }),
      reminder_cadence_days: reminderDays ? Number(reminderDays) : undefined,
      expires_at: expiresAt || undefined,
    }),
    onSuccess: (data: any) => {
      toast(`Sent to ${data.requests.length} recipients`, "success");
      qc.invalidateQueries({ queryKey: ["esign-batches"] });
      setTemplateId(""); setTitle(""); setBulkRecipients([{ mode: "internal" }]);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Bulk send failed", "error"),
  });

  const singleReady = template && template.roles.every((r: any) => {
    const rec = recipients[r.role];
    if (!rec) return false;
    return rec.mode === "internal" ? !!rec.internal_user_id : !!(rec.external_name && rec.external_email);
  });
  const bulkReady = template && bulkRecipients.length > 0 && bulkRecipients.every(r =>
    r.mode === "internal" ? !!r.internal_user_id : !!(r.external_name && r.external_email));

  if ((templates as any[]).length === 0) {
    return <EmptyState icon="✍️" title="No templates to send yet" hint="Create one in the Templates tab first — upload a PDF and place its signature fields." />;
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={card}>
        <label style={label}>Template</label>
        <select style={input} value={templateId} onChange={e => { setTemplateId(e.target.value ? Number(e.target.value) : ""); setBulkMode(false); setRecipients({}); }}>
          <option value="">Select a template…</option>
          {(templates as any[]).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        {template && (
          <>
            <div style={{ marginTop: 12 }}>
              <label style={label}>Title (optional)</label>
              <input style={input} value={title} onChange={e => setTitle(e.target.value)} placeholder={template.name} />
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Reminder every (days, optional)</label>
                <input style={input} type="number" min={1} value={reminderDays} onChange={e => setReminderDays(e.target.value)} placeholder="e.g. 3" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Expires on (optional)</label>
                <input style={input} type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
              </div>
            </div>

            {canBulk && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12, color: "#475569", cursor: "pointer" }}>
                <input type="checkbox" checked={bulkMode} onChange={e => setBulkMode(e.target.checked)} />
                Send to multiple recipients (bulk send) — each gets their own independent copy
              </label>
            )}
          </>
        )}
      </div>

      {template && !bulkMode && (
        <div style={card}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, color: "#0f172a" }}>Recipients</h3>
          <div style={{ display: "grid", gap: 12 }}>
            {template.roles.map((r: any) => (
              <RecipientRow key={r.role} roleLabel={r.role} employees={employees as any[]}
                value={recipients[r.role]}
                onChange={v => setRecipients(prev => ({ ...prev, [r.role]: v }))} />
            ))}
          </div>
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <AsyncButton style={{ ...btnPrimary, opacity: singleReady ? 1 : 0.5 }} disabled={!singleReady}
              onClick={() => sendMut.mutateAsync()}>
              Send for Signature
            </AsyncButton>
          </div>
        </div>
      )}

      {template && bulkMode && (
        <div style={card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14, color: "#0f172a" }}>Recipients ({bulkRecipients.length})</h3>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#64748b" }}>
            Every recipient signs as "{template.roles[0].role}", independently.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            {bulkRecipients.map((r, idx) => (
              <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <RecipientRow roleLabel="" employees={employees as any[]} value={r}
                    onChange={v => setBulkRecipients(prev => prev.map((p, i) => i === idx ? v : p))} compact />
                </div>
                <button onClick={() => setBulkRecipients(prev => prev.filter((_, i) => i !== idx))}
                  style={{ border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: 13, padding: "9px 4px" }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button style={{ ...btnGhost, marginTop: 10 }} onClick={() => setBulkRecipients(prev => [...prev, { mode: "internal" }])}>
            + Add recipient
          </button>
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <AsyncButton style={{ ...btnPrimary, opacity: bulkReady ? 1 : 0.5 }} disabled={!bulkReady}
              onClick={() => bulkSendMut.mutateAsync()}>
              Bulk Send to {bulkRecipients.length}
            </AsyncButton>
          </div>
        </div>
      )}
    </div>
  );
}

interface RecipientRowProps {
  roleLabel: string;
  employees: any[];
  value?: { mode: "internal" | "external"; internal_user_id?: number; external_name?: string; external_email?: string };
  onChange: (v: any) => void;
  compact?: boolean;
}

const RecipientRow: React.FC<RecipientRowProps> = ({ roleLabel, employees, value, onChange, compact }) => {
  const mode = value?.mode || "internal";
  return (
    <div>
      {roleLabel && <div style={{ fontSize: 12, fontWeight: 700, color: "#1e3a8a", marginBottom: 6 }}>{roleLabel}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <select style={{ ...input, width: compact ? 110 : 130 }} value={mode}
          onChange={e => onChange({ mode: e.target.value as "internal" | "external" })}>
          <option value="internal">Employee</option>
          <option value="external">External</option>
        </select>
        {mode === "internal" ? (
          <select style={{ ...input, flex: 1, minWidth: 180 }} value={value?.internal_user_id || ""}
            onChange={e => onChange({ mode: "internal", internal_user_id: Number(e.target.value) })}>
            <option value="">Select employee…</option>
            {employees.map((emp: any) => <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>)}
          </select>
        ) : (
          <>
            <input style={{ ...input, flex: 1, minWidth: 140 }} placeholder="Full name" value={value?.external_name || ""}
              onChange={e => onChange({ mode: "external", external_name: e.target.value, external_email: value?.external_email })} />
            <input style={{ ...input, flex: 1, minWidth: 180 }} placeholder="Email" value={value?.external_email || ""}
              onChange={e => onChange({ mode: "external", external_name: value?.external_name, external_email: e.target.value })} />
          </>
        )}
      </div>
    </div>
  );
};

// ── Sent Documents tab ───────────────────────────────────────────────────────

function SentTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { data: requests = [], isLoading } = useQuery({ queryKey: ["esign-requests"], queryFn: getEsignRequests });
  const { data: batches = [] } = useQuery({ queryKey: ["esign-batches"], queryFn: getEsignBatches });
  const [openBatch, setOpenBatch] = useState<string | null>(null);
  const { data: batchDetail } = useQuery({
    queryKey: ["esign-batch", openBatch], queryFn: () => getEsignBatch(openBatch!), enabled: !!openBatch,
  });

  const voidMut = useMutation({
    mutationFn: (id: number) => voidEsignRequest(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["esign-requests"] }); toast("Request voided", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to void", "error"),
  });

  async function handleDownload(id: number, title: string) {
    try {
      const { blob } = await downloadEsignRequestPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${title}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Failed to download", "error");
    }
  }

  const nonBatchRequests = (requests as any[]).filter(r => !r.batch_id);

  if (!isLoading && nonBatchRequests.length === 0 && (batches as any[]).length === 0) {
    return <EmptyState icon="📤" title="Nothing sent yet" hint="Head to the Send tab to send your first document for signature." />;
  }

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {isLoading && <p style={{ fontSize: 13, color: "#64748b" }}>Loading…</p>}
      {(batches as any[]).length > 0 && (
        <div>
          <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#0f172a" }}>Bulk Sends</h3>
          <div style={{ display: "grid", gap: 8 }}>
            {(batches as any[]).map(b => (
              <div key={b.batch_id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                  onClick={() => setOpenBatch(openBatch === b.batch_id ? null : b.batch_id)}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{b.title}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{b.total} recipients</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {Object.entries(b.counts).map(([status, count]) => (
                      <span key={status} style={{ fontSize: 11 }}>
                        <StatusBadge status={status} /> ×{count as number}
                      </span>
                    ))}
                  </div>
                </div>
                {openBatch === b.batch_id && batchDetail && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0", display: "grid", gap: 6 }}>
                    {(batchDetail as any).requests.map((r: any) => (
                      <div key={r.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: "#475569" }}>
                          {r.recipients[0]?.external_name || r.recipients[0]?.external_email || `Employee #${r.recipients[0]?.internal_user_id}`}
                        </span>
                        <StatusBadge status={r.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#0f172a" }}>Sent Documents</h3>
        {!isLoading && nonBatchRequests.length === 0 && (
          <p style={{ fontSize: 12, color: "#94a3b8" }}>No individual documents sent yet.</p>
        )}
        <div style={{ display: "grid", gap: 8 }}>
          {nonBatchRequests.map(r => (
            <div key={r.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{r.title}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                    {r.recipients.map((rec: any) => (
                      <span key={rec.id} style={{ fontSize: 11, color: "#64748b" }}>
                        {rec.role}: <StatusBadge status={rec.status} />
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <StatusBadge status={r.status} />
                  {r.status === "completed" && (
                    <button style={btnGhost} onClick={() => handleDownload(r.id, r.title)}>⬇ Download</button>
                  )}
                  {!["completed", "voided", "declined", "expired"].includes(r.status) && (
                    <AsyncButton style={{ ...btnGhost, color: "#dc2626", borderColor: "#fecaca" }}
                      onClick={async () => {
                        if (!(await confirm({ title: "Void this request?", message: "Recipients will no longer be able to sign it.", confirmLabel: "Void" }))) return;
                        await voidMut.mutateAsync(r.id);
                      }}>
                      Void
                    </AsyncButton>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── To Sign tab ───────────────────────────────────────────────────────────────

function ToSignTab() {
  const navigate = useNavigate();
  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["esign-my-tasks"], queryFn: getMyEsignTasks });

  const pending = (tasks as any[]).filter(t => ["notified", "viewed"].includes(t.status));
  const done = (tasks as any[]).filter(t => !["notified", "viewed"].includes(t.status));

  if (!isLoading && pending.length === 0 && done.length === 0) {
    return <EmptyState icon="✅" title="Nothing to sign" hint="Any document sent to you for signature will show up here." />;
  }

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {isLoading && <p style={{ fontSize: 13, color: "#64748b" }}>Loading…</p>}
      <div>
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#0f172a" }}>Awaiting Your Signature</h3>
        {!isLoading && pending.length === 0 && <p style={{ fontSize: 12, color: "#94a3b8" }}>Nothing waiting on you right now.</p>}
        <div style={{ display: "grid", gap: 8 }}>
          {pending.map(t => (
            <div key={t.recipient_id} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{t.title}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>as {t.role}</div>
              </div>
              <button style={btnPrimary} onClick={() => navigate(`/esign/${t.request_id}/${t.recipient_id}`)}>
                Review &amp; Sign
              </button>
            </div>
          ))}
        </div>
      </div>

      {done.length > 0 && (
        <div>
          <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#0f172a" }}>History</h3>
          <div style={{ display: "grid", gap: 8 }}>
            {done.map(t => (
              <div key={t.recipient_id} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>as {t.role}</div>
                </div>
                <StatusBadge status={t.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
